import { useState, useEffect } from 'react';
import api from '../../services/api';
import { X, FolderOpen } from 'lucide-react';
import WheelPicker from '../shared/WheelPicker';

export default function SectionModal({ isOpen, onClose, onSuccess, moduleId, section = null }) {
  const [formData, setFormData] = useState({
    title_pt: '',
    description_pt: '',
    order_index: 0
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (section) {
      setFormData({
        title_pt: section.title_pt || '',
        description_pt: section.description_pt || '',
        order_index: section.order_index || 0
      });
    } else {
      setFormData({
        title_pt: '',
        description_pt: '',
        order_index: 0
      });
    }
  }, [section]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      let data;
      if (section) {
        // Editar
        const response = await api.put(`/sections/${section.id}`, formData);
        data = response.data;
      } else {
        // Criar
        const response = await api.post('/sections', {
          ...formData,
          module_id: moduleId
        });
        data = response.data;
      }

      onSuccess(data);
      onClose();
      
      if (!section) {
        setFormData({
          title_pt: '',
          description_pt: '',
          order_index: 0
        });
      }
    } catch (err) {
      console.error('Erro ao salvar seção:', err);
      setError(err.response?.data?.error || 'Erro ao salvar seção');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/80 backdrop-blur-sm"
        onClick={onClose}
      ></div>

      {/* Modal */}
      <div className="relative bg-background-800 border border-gray-700 rounded-3xl p-8 max-w-2xl w-full">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-primary-500/20 flex items-center justify-center">
              <FolderOpen size={24} className="text-primary-500" />
            </div>
            <h2 className="text-2xl font-bold text-text-primary">
              {section ? 'Editar Seção' : 'Nova Seção'}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-500/10 border border-red-500/50 rounded-2xl text-red-400">
            {error}
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Título */}
          <div>
            <label className="block text-sm font-semibold text-text-primary mb-2">
              Título da Seção
            </label>
            <input
              type="text"
              value={formData.title_pt}
              onChange={(e) => setFormData({...formData, title_pt: e.target.value})}
              className="w-full px-4 py-3 bg-background-900 border-2 border-gray-700 rounded-2xl focus:border-primary-500 focus:ring-4 focus:ring-primary-500/20 transition-all text-text-primary"
              placeholder="Ex: Introdução ao Marketing Digital"
              required
            />
          </div>

          {/* Descrição */}
          <div>
            <label className="block text-sm font-semibold text-text-primary mb-2">
              Descrição (opcional)
            </label>
            <textarea
              value={formData.description_pt}
              onChange={(e) => setFormData({...formData, description_pt: e.target.value})}
              className="w-full px-4 py-3 bg-background-900 border-2 border-gray-700 rounded-2xl focus:border-primary-500 focus:ring-4 focus:ring-primary-500/20 transition-all text-text-primary resize-none"
              rows="3"
              placeholder="Descreva o conteúdo desta seção..."
            />
          </div>

          {/* Ordem */}
          <WheelPicker
            value={formData.order_index}
            onChange={(newValue) => setFormData({...formData, order_index: newValue})}
            min={1}
            max={20}
            label="Ordem da Seção"
          />

          {/* Botões */}
          <div className="flex gap-4 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 bg-background-700 hover:bg-background-600 text-text-primary px-6 py-3 rounded-2xl font-semibold transition-all"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 bg-primary-500 hover:bg-primary-600 text-white px-6 py-3 rounded-2xl font-semibold transition-all disabled:opacity-50 shadow-lg shadow-primary-500/20"
            >
              {loading ? 'Salvando...' : section ? 'Atualizar Seção' : 'Criar Seção'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
